import { ReactNode } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'

import { ColumnConfig, getColumnValue } from '@avantstay/data-grid'

import { Parser } from '@json2csv/plainjs'
import { trackError, trackEvent } from '@tracking/customEventReport'

interface Options {
  filename?: string
  useTitles?: boolean
  useRenderers?: boolean
}

interface ExportDataToCsvProps<RowType> {
  data: RowType[]
  columns: ColumnConfig<RowType>[]
  trackingInfo: {
    alias: string
    totalRows?: number
    queryArgs?: Record<string, any>
  }
  options?: Options
}

const DEFAULT_OPTIONS: Options = {
  filename: 'grid-data',
  useTitles: false,
  useRenderers: false,
}

/**
 * Removes unpaired UTF-16 surrogates that cause encodeURIComponent to fail.
 * Surrogates are code points in the range U+D800 to U+DFFF.
 * Valid pairs: high surrogate (D800-DBFF) followed by low surrogate (DC00-DFFF)
 */
const sanitizeForUri = (str: string): string => {
  return str.replace(
    /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g,
    '\uFFFD',
  )
}

const hasFileExt = (fileName: string): boolean => {
  const allowedExts = ['.pdf', '.jpg', '.jpeg', '.png', '.txt', '.xls', '.csv']
  return allowedExts.map(ext => fileName.includes(ext)).filter(has => has).length > 0
}

const downloadFile = (uri: string, fileName: string) => {
  fetch(uri)
    .then(response => response.blob())
    .then(blob => {
      const newUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const fileNameWithExt = hasFileExt(fileName) ? fileName : `${fileName}.pdf`

      link.target = '_blank'
      link.href = newUrl
      link.download = fileNameWithExt

      document.body.appendChild(link)
      link.onclick = function onClick(e) {
        e.stopPropagation()
      }
      link.click()
      document.body.removeChild(link)
    })
}

const getInnerText = (reactNode: ReactNode): string => {
  if (!reactNode) return ''

  // Handle primitive types
  if (typeof reactNode === 'string' || typeof reactNode === 'number') {
    return String(reactNode)
  }

  // Handle React elements
  const markup = renderToStaticMarkup(reactNode as React.ReactElement)
  // Remove HTML tags to get just text
  return markup.replace(/<[^>]*>/g, '')
}

export function exportDataToCSV<RowType = any>({
  data,
  columns,
  trackingInfo,
  options = DEFAULT_OPTIONS,
}: ExportDataToCsvProps<RowType>) {
  trackEvent(`Exporting data to CSV: ${trackingInfo.alias}`, {
    columns: columns.map(column => column.columnKey),
    queryArgs: trackingInfo.queryArgs,
    totalRows: trackingInfo.totalRows,
  })

  try {
    const { filename, useTitles, useRenderers } = options

    const formattedData = data.map((rowData: RowType, rowIndex: number) =>
      columns.reduce(
        (acc, column) => {
          const value = getColumnValue({
            columnKey: column.columnKey,
            rowData,
          })
          acc[column.title || column.columnKey] =
            useRenderers && column.renderer
              ? getInnerText(column.renderer({ value, rowData, rowIndex }))
              : value
          return acc
        },
        {} as Record<string, string | number>,
      ),
    )

    const parser = new Parser({
      fields: columns.map(column =>
        useTitles ? column.title || column.columnKey : column.columnKey,
      ),
    })

    const csvData = parser.parse(useTitles ? formattedData : data)
    const uri = `data:text/csv;charset=utf-8,${encodeURIComponent(sanitizeForUri(csvData))}`
    downloadFile(uri, `${filename || DEFAULT_OPTIONS.filename}.csv`)
  } catch (error) {
    console.error(error)
    trackError(`Error while exporting data to CSV: ${trackingInfo.alias}`, { error })
    throw Error('Error while exporting data to CSV')
  }
}
